import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { SharedService } from 'src/app/core/services/shared.service';
import Swal from 'sweetalert2';

interface DiaQuincena {
  FEC: string;
  DIA: string;
  HOR_INI_BASE: number;
  HOR_FIN_BASE: number;
  _HOR_INIEdit?: string;
  _HOR_FINEdit?: string;
  TIP_JORN: string;
  ES_DESCANSO: boolean;
  ES_FERIADO: boolean;
  MARCAJES: Marcaje[];
}

interface Marcaje {
  PLA98CON: string;
  HLI: number;
  HLF: number;
  HAI: number;
  HAF: number;
  EST: string;
  PLA28CON: string;
  TSTAMP: string;
  // edición local:
  _hliEdit?: string;
  _hlfEdit?: string;
  _haiEdit?: string;
  _hafEdit?: string;
  _dirty?: boolean;
}

interface LineaPreview {
  FEC: string;
  COD: string;
  DES: string;
  CAN: number;
  FAC: number;
  RAT: number;
  MON: number;
}

interface TotalConcepto {
  COD: string;
  DES: string;
  CAN: number;
  MON: number;
}

@Component({
  selector: 'app-marcajes-quincena',
  templateUrl: './marcajes-quincena.component.html',
  styleUrls: ['./marcajes-quincena.component.scss']
})
export class MarcajesQuincenaComponent implements OnInit {

  @Input() WsLlaves: any;
  @Output() cancelar = new EventEmitter<void>();

  private urlQuincena = 'api/ws/rhu/Marcajes_WS/Quincena/regMarcajes';
  private urlBulk     = 'api/ws/rhu/Marcajes_WS/BulkSave/regMarcajes';
  private urlPreview  = 'api/ws/rhu/HorasExtras_WS/Preview/regHorasExtras';
  private urlAplicar  = 'api/ws/rhu/HorasExtras_WS/Aplicar/regHorasExtras';
  private urlReversar = 'api/ws/rhu/HorasExtras_WS/Reversar/regHorasExtras';

  PAD10COD = '';
  PAD10NOM = '';
  FEC_INI  = '';
  FEC_FIN  = '';
  PLA08COD = '';
  PLA28CON = '';

  dias: DiaQuincena[] = [];
  preview: LineaPreview[] = [];
  totalesConcepto: TotalConcepto[] = [];
  totalGeneral = 0;

  cargando        = false;
  calculando      = false;
  guardando       = false;
  aplicando       = false;
  previewVisible  = false;
  permisos: any   = { GRABAR: 'N' };

  constructor(private dataService: SharedService) {}

  ngOnInit(): void {
    this.PAD10COD = this.WsLlaves[0].PAD10COD;
    this.FEC_INI  = this.convertirFecha(this.WsLlaves[0].RHU26FIN)  || this.primerDiaQuincenaActual();
    this.FEC_FIN  = this.convertirFecha(this.WsLlaves[0].RHU26FFI)  || this.ultimoDiaQuincenaActual();
    this.PLA08COD = this.WsLlaves[0].PLA08COD;
    this.PLA28CON = this.WsLlaves[0].PLA28CON;
    this.permisos = { GRABAR: 'S' };
    if (this.PAD10COD) this.cargarQuincena();
  }

  //---------------------------------------------------------------
  // Carga inicial y persistencia de marcajes
  //---------------------------------------------------------------

  cargarQuincena() {
    if (!this.PAD10COD || !this.FEC_INI || !this.FEC_FIN) {
      Swal.fire('Atención', 'Seleccione empleado y rango de fechas', 'warning');
      return;
    }
    this.cargando = true;
    this.previewVisible = false;
    const body = {
      FILTROS_OBLIGATORIOS: [{
        PAD10COD: this.PAD10COD, FEC_INI: this.FEC_INI, FEC_FIN: this.FEC_FIN
      }]
    };
    this.dataService.ConsultarWS(this.urlQuincena, JSON.stringify(body)).subscribe(
      (resp: any) => {
        const d = resp.DATA || resp || {};
        this.PAD10NOM = d.PAD10NOM || '';
        this.dias     = (d.DIAS || []).map((dia: DiaQuincena) => ({
          ...dia,
          MARCAJES: (dia.MARCAJES || []).map(m => this.prepararMarcaje(m))
        }));
        this.dias.forEach((dia: DiaQuincena) => {
          dia._HOR_INIEdit = this.hhmmToTexto(dia.HOR_INI_BASE);
          dia._HOR_FINEdit = this.hhmmToTexto(dia.HOR_FIN_BASE);
        });
        this.cargando = false;
      },
      (err: any) => {
        this.cargando = false;
        Swal.fire('Error', err || 'No se pudo cargar la quincena', 'error');
      });
  }

  private prepararMarcaje(m: Marcaje): Marcaje {
    return {
      ...m,
      _hliEdit: this.hhmmToTexto(m.HLI),
      _hlfEdit: this.hhmmToTexto(m.HLF),
      _haiEdit: this.hhmmToTexto(m.HAI),
      _hafEdit: this.hhmmToTexto(m.HAF),
      _dirty: false
    };
  }

  agregarMarcaje(dia: DiaQuincena) {
    if (this.permisos.GRABAR !== 'S') return;
    dia.MARCAJES.push(this.prepararMarcaje({
      PLA98CON: '', HLI: 0, HLF: 0, HAI: 0, HAF: 0, EST: 'C', PLA28CON: '', TSTAMP: ''
    }));
  }

  quitarMarcaje(dia: DiaQuincena, idx: number) {
    if (this.permisos.GRABAR !== 'S') return;
    if (dia.MARCAJES[idx].EST === 'P') {
      Swal.fire('Atención', 'Este marcaje ya está pagado. Reverse primero la planilla.', 'warning');
      return;
    }
    dia.MARCAJES.splice(idx, 1);
  }

  onCeldaEdit(m: Marcaje) {
    if (m.EST === 'P') return;
    m._dirty = true;
  }

  guardar() {
    if (this.permisos.GRABAR !== 'S') return;
    this.guardando = true;
    const payload: any[] = [];
    for (const dia of this.dias) {
      dia.HOR_INI_BASE = this.textoToHHMM(dia._HOR_INIEdit);
      dia.HOR_FIN_BASE = this.textoToHHMM(dia._HOR_FINEdit);
      for (const m of dia.MARCAJES) {
        if (m.EST === 'P') continue;
        payload.push({
          FEC:      dia.FEC,
          PLA98CON: m.PLA98CON,
          HLI:      this.textoToHHMM(m._hliEdit),
          HLF:      this.textoToHHMM(m._hlfEdit),
          HAI:      this.textoToHHMM(m._haiEdit),
          HAF:      this.textoToHHMM(m._hafEdit),
          TSTAMP:   m.TSTAMP,
          HOR_INI_BASE : dia.HOR_INI_BASE,
          HOR_FIN_BASE : dia.HOR_FIN_BASE
        });
      }
    }
    const body = {
      FILTROS_OBLIGATORIOS: [{ PAD10COD: this.PAD10COD, FEC_INI: this.FEC_INI, FEC_FIN: this.FEC_FIN }],
      MARCAJES: payload
    };
    this.dataService.ConsultarWS(this.urlBulk, JSON.stringify(body)).subscribe(
      () => {
        this.guardando = false;
        Swal.fire({ icon: 'success', title: 'Guardado', timer: 1200, showConfirmButton: false });
        this.cargarQuincena();
      },
      (err: any) => {
        this.guardando = false;
        Swal.fire('Error', err || 'No se pudo guardar', 'error');
      });
  }

  //---------------------------------------------------------------
  // Preview / Aplicar / Reversar
  //---------------------------------------------------------------

  calcular() {
    if (this.hayDirty()) {
      Swal.fire('Atención', 'Guarde los marcajes antes de calcular', 'warning');
      return;
    }
    this.calculando = true;
    const body = {
      FILTROS_OBLIGATORIOS: [{ PAD10COD: this.PAD10COD, FEC_INI: this.FEC_INI, FEC_FIN: this.FEC_FIN }]
    };
    this.dataService.ConsultarWS(this.urlPreview, JSON.stringify(body)).subscribe(
      (resp: any) => {
        const d = resp.DATA || resp || {};
        this.preview        = d.LINEAS || [];
        this.totalesConcepto = d.TOTALES_CONCEPTO || [];
        this.totalGeneral    = d.TOTAL_GENERAL || 0;
        this.previewVisible  = true;
        this.calculando = false;
      },
      (err: any) => {
        this.calculando = false;
        Swal.fire('Error', err || 'No se pudo calcular', 'error');
      });
  }

  async aplicar() {
    if (!this.previewVisible) {
      Swal.fire('Atención', 'Calcule primero el preview', 'warning');
      return;
    }
    const { value: pla28con } = await Swal.fire({
      title: 'Aplicar a planilla',
      html: `Consecutivo de planilla destino:`,
      input: 'text',
      inputValue: this.PLA28CON,
      showCancelButton: true,
      confirmButtonText: 'Aplicar',
      cancelButtonText: 'Cancelar',
      inputValidator: v => v ? null : 'Indique el consecutivo'
    });
    if (!pla28con) return;
    if (pla28con != this.PLA28CON) {
      alert("Lo lamento, debe confirmar el número de planilla");
      return;
    } 
    this.aplicando = true;
    const body = {
      FILTROS_OBLIGATORIOS: [{
        PAD10COD: this.PAD10COD, FEC_INI: this.FEC_INI, FEC_FIN: this.FEC_FIN,
        PLA28CON: pla28con, PLA08COD: this.PLA08COD
      }]
    };
    this.dataService.ConsultarWS(this.urlAplicar, JSON.stringify(body)).subscribe(
      (resp: any) => {
        this.aplicando = false;
        const d = resp.DATA || resp || {};
        Swal.fire('Aplicado', `${d.LINEAS_PLA33ARC || 0} líneas creadas`, 'success');
        this.PLA28CON = pla28con;
        this.cargarQuincena();
      },
      (err: any) => {
        this.aplicando = false;
        Swal.fire('Error', err || 'No se pudo aplicar', 'error');
      });
  }

  async reversar() {
    if (!this.PLA28CON) {
      Swal.fire('Atención', 'Indique consecutivo de planilla a reversar', 'warning');
      return;
    }
    const c = await Swal.fire({
      title: '¿Reversar aplicación?',
      text: `Se borrarán las líneas de la planilla ${this.PLA28CON}.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Reversar'
    });
    if (!c.isConfirmed) return;
    const body = {
      FILTROS_OBLIGATORIOS: [{
        PAD10COD: this.PAD10COD, FEC_INI: this.FEC_INI, FEC_FIN: this.FEC_FIN,
        PLA28CON: this.PLA28CON
      }]
    };
    this.dataService.ConsultarWS(this.urlReversar, JSON.stringify(body)).subscribe(
      () => {
        Swal.fire('Reversado', 'Las líneas fueron anuladas', 'success');
        this.cargarQuincena();
      },
      (err: any) => Swal.fire('Error', err || 'No se pudo reversar', 'error'));
  }

  //---------------------------------------------------------------
  // Helpers
  //---------------------------------------------------------------

  private hhmmToTexto(hhmm: number): string {
    if (!hhmm || hhmm < 0) return '';
    const h = Math.floor(hhmm / 100);
    const m = hhmm % 100;
    return `${this.pad(h)}:${this.pad(m)}`;
  }

  private textoToHHMM(s: string | undefined): number {
    if (!s) return 0;
    const t = s.trim();
    if (!t.includes(':')) {
      const n = parseInt(t, 10);
      return isNaN(n) ? 0 : n;
    }
    const [h, m] = t.split(':');
    const hh = parseInt(h, 10) || 0;
    const mm = parseInt(m, 10) || 0;
    return hh * 100 + mm;
  }

  private pad(n: number): string { return n < 10 ? '0' + n : '' + n; }

  private hayDirty(): boolean {
    return this.dias.some(d => d.MARCAJES.some(m => m._dirty));
  }

  private convertirFecha(fechaOriginal: string): string {
  if (!fechaOriginal) return '';

  return fechaOriginal.replace(' 00:00:00','');
}

  private primerDiaQuincenaActual(): string {
    const d = new Date();
    const day = d.getDate() <= 15 ? 1 : 16;
    return `${d.getFullYear()}-${this.pad(d.getMonth() + 1)}-${this.pad(day)}`;
  }

  private ultimoDiaQuincenaActual(): string {
    const d = new Date();
    if (d.getDate() <= 15) {
      return `${d.getFullYear()}-${this.pad(d.getMonth() + 1)}-15`;
    }
    const ult = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
    return `${d.getFullYear()}-${this.pad(d.getMonth() + 1)}-${this.pad(ult)}`;
  }

  emitirCancelar() { this.cancelar.emit(); }

  // Para el template
  trackByFec  = (_: number, d: DiaQuincena) => d.FEC;
  trackByLin  = (_: number, l: LineaPreview) => `${l.FEC}-${l.COD}-${_}`;
  tienePagados(d: DiaQuincena): boolean { return d.MARCAJES.some(m => m.EST === 'P'); }

  checkMarcaje(d: DiaQuincena){
    let bol = false;
    if (d.MARCAJES.length > 0) {
      if (d.MARCAJES[0].EST === "P") {
        bol = true;
      }
    }

    return bol;
  }


}
